import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, rbacAPI } from '../lib/api';
import axios from 'axios';

// Same-origin backend (see lib/api.js) - avoids www/apex CORS redirect issues.
const BACKEND_URL = window.location.origin;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msConfig, setMsConfig] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [accessibleModules, setAccessibleModules] = useState([]);

  // Fetch user permissions from RBAC API
  const fetchPermissions = async () => {
    try {
      const res = await rbacAPI.getMyPermissions();
      setPermissions(res.data.permissions);
      setAccessibleModules(res.data.accessible_modules || []);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      return null;
    }
  };

  // Live refresh: re-pull the current user + permissions so menu/access updates
  // without requiring a logout/login when an admin changes their role or access.
  const refreshAccess = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const [meRes, permRes] = await Promise.all([
        authAPI.getMe(),
        rbacAPI.getMyPermissions(),
      ]);
      setUser(meRes.data);
      localStorage.setItem('user', JSON.stringify(meRes.data));
      setPermissions(permRes.data.permissions);
      setAccessibleModules(permRes.data.accessible_modules || []);
    } catch (error) {
      // Transient/network errors are ignored; next tick will retry.
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      // Load MS config (never let this block app render - use a short timeout)
      try {
        const res = await axios.get(`${BACKEND_URL}/api/auth/ms-config`, { timeout: 8000 });
        console.log('✅ MS Config Response:', res.data);
        if (res.data && res.data.enabled === true) {
          setMsConfig(res.data);
          console.log('✅ MS SSO configured successfully');
        } else {
          console.log('⚠️  MS SSO is disabled or not configured:', res.data);
        }
      } catch (e) {
        console.error('❌ Failed to fetch MS config:', e.message, e.response?.data);
        console.error('Backend URL:', BACKEND_URL);
      }

      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      
      if (token && savedUser) {
        try {
          // Verify token and get fresh user data
          const userRes = await authAPI.getMe();
          setUser(userRes.data);
          
          // Fetch permissions
          try {
            const permRes = await rbacAPI.getMyPermissions();
            setPermissions(permRes.data.permissions);
            setAccessibleModules(permRes.data.accessible_modules || []);
          } catch (err) {
            console.error('Failed to fetch permissions:', err);
          }
        } catch {
          // Token invalid - clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
          setPermissions(null);
          setAccessibleModules([]);
        }
      }
    };
    
    // Guarantee the app never stays stuck on the loading spinner, even if a
    // network request hangs. The login page must always be reachable.
    initAuth().finally(() => setLoading(false));
  }, []);

  // Live permission refresh: poll periodically and on tab focus so access
  // changes made by an admin take effect without a manual re-login.
  useEffect(() => {
    if (!user?.id) return;
    const interval = setInterval(refreshAccess, 60000);
    const onFocus = () => refreshAccess();
    const onVisible = () => { if (!document.hidden) refreshAccess(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user?.id]);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    // Fetch permissions after login
    await fetchPermissions();
    return userData;
  };

  const msLogin = async (code, redirectUri) => {
    const res = await axios.post(`${BACKEND_URL}/api/auth/ms/login`, { code, redirect_uri: redirectUri });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    // Fetch permissions after login
    await fetchPermissions();
    return userData;
  };

  const register = async (data) => {
    const res = await authAPI.register(data);
    const { access_token, user: userData } = res.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    await fetchPermissions();
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setPermissions(null);
    setAccessibleModules([]);
  };

  // Role checks
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin' || isSuperAdmin;
  const isHR = user?.role === 'hr' || isAdmin;
  const isAccounts = user?.role === 'accounts' || isAdmin;
  const isSales = user?.role === 'sales' || isAdmin;
  const isPerformanceEngineer = user?.role === 'performance_engineer';
  const isEmployee = user?.role === 'employee';
  const isGuest = user?.role === 'guest';
  
  // Dynamic permission check using RBAC
  const hasModuleAccess = (module) => {
    if (isSuperAdmin) return true;
    const ids = (accessibleModules || []).map((m) => (typeof m === 'object' ? m.id : m));
    return ids.includes(module);
  };
  
  // Permission checks - super_admin has all permissions
  const canApproveLeave = isSuperAdmin || isAdmin || isHR;
  const canApproveExpense = isSuperAdmin || isAdmin || isHR || isAccounts;
  const canApproveTravel = isSuperAdmin || isAdmin || isHR || isAccounts;
  const canManageEmployees = isSuperAdmin || isAdmin || isHR;
  const canManageAssets = isSuperAdmin || isAdmin;
  const canManageCRM = isSuperAdmin || isAdmin || isSales;
  const canValidateLeads = isSuperAdmin || isAdmin || isPerformanceEngineer;
  const canViewPayroll = isSuperAdmin || isAdmin || isHR || isAccounts || isEmployee;
  const canCreateRequests = isEmployee || isHR || isAdmin || isSuperAdmin || isSales;

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      msLogin,
      register, 
      logout, 
      msConfig,
      // Dynamic RBAC
      permissions,
      accessibleModules,
      hasModuleAccess,
      refreshAccess,
      // Role checks
      isSuperAdmin,
      isAdmin, 
      isHR,
      isAccounts,
      isSales,
      isPerformanceEngineer,
      isEmployee,
      isGuest,
      // Permission checks
      canApproveLeave,
      canApproveExpense,
      canApproveTravel,
      canManageEmployees,
      canManageAssets,
      canManageCRM,
      canValidateLeads,
      canViewPayroll,
      canCreateRequests,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
